import Cookies from "js-cookie";

export const RESTAURANT_DROPDOWN_CACHE_NAMESPACE = "foodoor_restaurant_dropdown_cache_v1";

const memoryCache = new Map();
const pendingRequests = new Map();

export const getRestaurantUserScope = () => {
  try {
    const raw = Cookies.getJSON("foodoor_token");
    if (!raw) return "anon";
    const uid = raw.user_id ?? "";
    const fid = raw.franchise_data ? (raw.franchise_data.id ?? raw.franchise_data) : "";
    const role = raw.is_superuser ? "su" : raw.is_franchise_admin ? "fa" : "other";
    return `${uid}_${fid}_${role}`;
  } catch (e) {
    return "unknown";
  }
};

export const normalizeDropdownParams = (params) => {
  if (!params) return "";
  const str = typeof params === "string" ? params : String(params);
  const searchParams = new URLSearchParams(str);
  searchParams.delete("cache_remove");
  searchParams.delete("date");
  searchParams.delete("_");
  const keys = Array.from(searchParams.keys()).sort();
  return keys
    .map((k) => {
      let v = searchParams.get(k);
      if (v === "1") v = "true";
      if (v === "0") v = "false";
      return `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
    })
    .join("&");
};

export const isDropdownQuery = (params) => {
  if (!params) return false;
  try {
    const str = typeof params === "string" ? params : String(params);
    const searchParams = new URLSearchParams(str);
    if (searchParams.has("id")) return false;
    const forDropdown = searchParams.get("for_dropdown");
    if (forDropdown === "false" || forDropdown === "0") return false;
    const reportDropDown = searchParams.get("report_drop_down");
    const blockedOutDropDown = searchParams.get("blocked_out_drop_down");
    return (
      forDropdown !== null ||
      reportDropDown !== null ||
      blockedOutDropDown !== null
    );
  } catch (e) {
    return false;
  }
};

export const makeDropdownCacheKey = (params) => {
  const scope = getRestaurantUserScope();
  const normalized = normalizeDropdownParams(params);
  return `${scope}|${normalized}`;
};

const readStorage = () => {
  try {
    const raw = localStorage.getItem(RESTAURANT_DROPDOWN_CACHE_NAMESPACE);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writeStorage = (entries) => {
  try {
    localStorage.setItem(RESTAURANT_DROPDOWN_CACHE_NAMESPACE, JSON.stringify(entries));
  } catch (e) {
    try {
      localStorage.removeItem(RESTAURANT_DROPDOWN_CACHE_NAMESPACE);
      localStorage.setItem(RESTAURANT_DROPDOWN_CACHE_NAMESPACE, JSON.stringify(entries));
    } catch (again) {}
  }
};

export const readRestaurantDropdownCache = (key) => {
  if (memoryCache.has(key)) {
    return memoryCache.get(key);
  }
  const storage = readStorage();
  if (storage && storage[key] !== undefined) {
    memoryCache.set(key, storage[key]);
    return storage[key];
  }
  return null;
};

export const writeRestaurantDropdownCache = (key, data) => {
  memoryCache.set(key, data);
  const storage = readStorage();
  storage[key] = data;
  writeStorage(storage);
};

export const clearRestaurantCache = () => {
  memoryCache.clear();
  pendingRequests.clear();
  try {
    localStorage.removeItem(RESTAURANT_DROPDOWN_CACHE_NAMESPACE);
  } catch (e) {}
};

export const fetchWithRestaurantDropdownCache = async (params, fetchFn) => {
  const key = makeDropdownCacheKey(params);
  const cached = readRestaurantDropdownCache(key);
  if (cached !== null) {
    return { data: cached, status: 200, statusText: "OK", headers: {}, config: {} };
  }

  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  const promise = (async () => {
    try {
      const response = await fetchFn();
      if (response && response.data && response.status >= 200 && response.status < 300) {
        writeRestaurantDropdownCache(key, response.data);
      }
      return response;
    } finally {
      pendingRequests.delete(key);
    }
  })();

  pendingRequests.set(key, promise);
  return promise;
};
